/** Dashboard time period ids (default: today). */
export const DASHBOARD_PERIOD_IDS = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  THIS_MONTH: 'this_month',
  THIS_QUARTER: 'this_quarter',
};

/** UI options for the period selector (order preserved). */
export const DASHBOARD_PERIOD_OPTIONS = [
  { id: DASHBOARD_PERIOD_IDS.TODAY, label: 'Today' },
  { id: DASHBOARD_PERIOD_IDS.THIS_WEEK, label: 'This Week' },
  { id: DASHBOARD_PERIOD_IDS.LAST_7_DAYS, label: 'Last 7 Days' },
  { id: DASHBOARD_PERIOD_IDS.LAST_30_DAYS, label: 'Last 30 Days' },
  { id: DASHBOARD_PERIOD_IDS.THIS_MONTH, label: 'This Month' },
  { id: DASHBOARD_PERIOD_IDS.THIS_QUARTER, label: 'This Quarter' },
];

const OPTION_LABEL_BY_ID = Object.fromEntries(DASHBOARD_PERIOD_OPTIONS.map((o) => [o.id, o.label]));

function safeToISO(val) {
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}

/** Human-readable period name for badges and subtitles (matches selector labels). */
export function getDashboardPeriodLabel(periodId) {
  return OPTION_LABEL_BY_ID[periodId] ?? 'Today';
}

/**
 * Returns { startDate, endDate } as UTC ISO strings for Supabase timestamp filters.
 * Period starts use local calendar boundaries converted via toISOString(); end is now (UTC).
 */
export function getDateRange(period) {
  const now = new Date();
  let start;

  switch (period) {
    case DASHBOARD_PERIOD_IDS.TODAY: {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      break;
    }
    case DASHBOARD_PERIOD_IDS.THIS_WEEK: {
      const d = new Date(now);
      const dow = d.getDay();
      const toMonday = dow === 0 ? -6 : 1 - dow;
      d.setDate(d.getDate() + toMonday);
      d.setHours(0, 0, 0, 0);
      start = d;
      break;
    }
    case DASHBOARD_PERIOD_IDS.LAST_7_DAYS: {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      d.setHours(0, 0, 0, 0);
      start = d;
      break;
    }
    case DASHBOARD_PERIOD_IDS.LAST_30_DAYS: {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      d.setHours(0, 0, 0, 0);
      start = d;
      break;
    }
    case DASHBOARD_PERIOD_IDS.THIS_MONTH: {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      break;
    }
    case DASHBOARD_PERIOD_IDS.THIS_QUARTER: {
      const q0 = Math.floor(now.getMonth() / 3) * 3;
      start = new Date(now.getFullYear(), q0, 1, 0, 0, 0, 0);
      break;
    }
    default: {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }
  }

  return { startDate: safeToISO(start), endDate: safeToISO(new Date()) };
}

/** Local calendar day bounds for "today" metrics (e.g. daily call goal). */
export function getLocalTodayIsoRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const range = { startDate: safeToISO(start), endDate: safeToISO(now) };
  if (import.meta.env.DEV) {
    console.log('[getLocalTodayIsoRange]', range);
  }
  return range;
}
